
import { mergeCompiledBundle } from "%/dynamic-components/render-engine/compiled-payload-store";
import { useMediaPlaybackStore } from "%/stores/media-playback-store";
import {
  COMPILED_STORE,
  currentDefaultsVersion,
  deleteCacheEntry,
  readCacheEntry,
  sweepCompiledCachesOncePerPage,
  writeCacheEntry,
} from "%/dynamic-components/render-engine/compiled-cache-storage";

const MEDIA_STATUS_READY = "ready";

export function compiledBaseUrl() {
  const base = window.blclient?.data?.links?.compiledBase;
  if (!base) {
    throw new Error(
      "compiledBaseUrl: links.compiledBase is empty on a compiled render surface. Every "
      + "/__compiled and /__defaults fetch would resolve to the platform root, where "
      + "those routes do not exist — the render surface would 404 and paint nothing. "
      + "The route that served this page must declare its destination link context "
      + "(BaseStudioController::setDestinationLinkContext).",
    );
  }
  return base;
}

export function describeCrossOriginCompiledBase(base) {
  const pageOrigin = window.location?.origin;
  if (new URL(base).origin === pageOrigin) {
    return "";
  }
  return `links.compiledBase (${base}) is cross-origin to this page (${pageOrigin}), so the request `
    + "would be CORS-blocked and was not sent. BaseStudioController::compiledBaseFor must return a "
    + "base on the host that served the page.";
}

function inlinedCompiledBundle(dcId) {
  return window.blclient?.data?.inlinedCompiledBundles?.[dcId] ?? null;
}

const PAGE_ACCESS_DENIED = "page_access_denied";

function cacheKey(env, dcId) {
  return `${env}:${dcId}`;
}

function evictEntry(env, dcId) {
  return deleteCacheEntry(COMPILED_STORE, cacheKey(env, dcId));
}

export function pageAccessRedirectUrl(redirect, location = window.location) {
  const returnTo = encodeURIComponent(location.href);
  if (redirect === "login") {
    return `/login?redirect=${returnTo}`;
  }
  if (redirect === "subscribe") {
    return `${compiledBaseUrl()}/subscribe?redirect=${returnTo}`;
  }
  return location.href;
}

function readEntry(env, dcId) {
  return readCacheEntry(COMPILED_STORE, cacheKey(env, dcId));
}

function hasExpired(entry) {
  return !entry.expiresAt || Date.now() >= entry.expiresAt * 1000;
}

function isFreshEntry(entry, version) {
  return !!entry && entry.version === version && !!entry.bundle?.payload && !hasExpired(entry);
}

function seedBundleMedia(bundle) {
  const playbackStore = useMediaPlaybackStore();
  playbackStore.seedResolvedMedia(bundle.resolvedMedia);
  playbackStore.seedResolvedMedia(bundle.provisionalMedia);
  playbackStore.seedUnresolvableMedia(bundle.unresolvableMedia);
}

function persistableBundle(bundle) {
  const { provisionalMedia, ...persistable } = bundle;
  return {
    ...persistable,
    provisionalMediaIds: Object.keys(provisionalMedia ?? {}).map(Number),
  };
}

function writeEntry(env, dcId, entry) {
  return writeCacheEntry(COMPILED_STORE, cacheKey(env, dcId), entry);
}

async function upgradeProvisionalMedia(env, dcId, version, mediaId, playback) {
  const entry = await readEntry(env, dcId);
  if (!entry || entry.version !== version || !entry.bundle) {
    return;
  }
  entry.bundle.resolvedMedia = { ...entry.bundle.resolvedMedia, [mediaId]: playback };
  entry.bundle.provisionalMediaIds = (entry.bundle.provisionalMediaIds ?? [])
      .filter(id => id !== mediaId);
  await writeEntry(env, dcId, entry);
}

function recheckProvisionalMedia(env, dcId, entry) {
  const playbackStore = useMediaPlaybackStore();
  (entry.bundle.provisionalMediaIds ?? []).forEach(mediaId => {
    playbackStore.resolveMediaPlayback(mediaId, playback => {
      if (playback.status !== MEDIA_STATUS_READY) {
        return;
      }
      upgradeProvisionalMedia(env, dcId, entry.version, mediaId, playback);
    });
  });
}

export async function hasFreshCompiledCache(env, dcId, version) {
  return isFreshEntry(await readEntry(env, dcId), version);
}

function reportCompiledFallback(dcId, reason) {
  console.error( // eslint-disable-line no-console
    "[DCE] the compiled payload was unusable and a compiled render surface has no live render "
    + "chain — the page cannot render",
    { dynamicComponentId: dcId, reason },
  );

  return { bundle: null, fromCache: false, reason };
}

const flightsInProgress = new Map();

export async function loadCompiledBundle(env, descriptor) {
  sweepCompiledCachesOncePerPage({ currentDefaultsVersion: currentDefaultsVersion() });
  const dcId = descriptor.dynamicComponentId;
  const inlined = inlinedCompiledBundle(dcId);
  if (inlined?.payload) {
    seedBundleMedia(inlined);
    return { bundle: inlined, fromCache: false };
  }

  const flightKey = `${cacheKey(env, dcId)}@${descriptor.version ?? ""}`;
  const alreadyInFlight = flightsInProgress.get(flightKey);

  if (alreadyInFlight) {
    return alreadyInFlight;
  }

  const flight = cachedOrFetchedBundle(env, dcId, descriptor.version);
  flightsInProgress.set(flightKey, flight);

  try {
    return await flight;
  } finally {
    flightsInProgress.delete(flightKey);
  }
}

async function cachedOrFetchedBundle(env, dcId, version) {
  const entry = await readEntry(env, dcId);
  if (isFreshEntry(entry, version)) {
    seedBundleMedia(entry.bundle);
    recheckProvisionalMedia(env, dcId, entry);
    return { bundle: entry.bundle, fromCache: true };
  }
  return fetchCompiledBundle(env, dcId);
}

async function handlePageAccessDenied(env, dcId, body) {
  await evictEntry(env, dcId);
  if (body?.error !== PAGE_ACCESS_DENIED) {
    return reportCompiledFallback(dcId, "the compiled payload responded 403");
  }
  window.location.assign(pageAccessRedirectUrl(body.redirect ?? null));
  return { bundle: null, fromCache: false, reason: `page access: ${body.reason}` };
}

async function fetchCompiledBundle(env, dcId) {
  const baseUrl = compiledBaseUrl();
  const crossOrigin = describeCrossOriginCompiledBase(baseUrl);
  if (crossOrigin) {
    return reportCompiledFallback(dcId, crossOrigin);
  }

  try {
    const res = await fetch(`${baseUrl}/__compiled/${dcId}`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (res.status === 403) {
      return await handlePageAccessDenied(env, dcId, await res.json().catch(() => ({})));
    }
    if (!res.ok) {
      const refusal = await res.json().then(body => body?.message ?? "").catch(() => "");
      return reportCompiledFallback(dcId, `the compiled payload responded ${res.status}${refusal ? `: ${refusal}` : ""}`);
    }
    const bundle = await res.json();
    if (!bundle?.payload) {
      return reportCompiledFallback(dcId, "the compiled payload had no `payload`");
    }
    seedBundleMedia(bundle);
    if (bundle.cacheable !== false) {
      await writeEntry(env, dcId, {
        version: bundle.version,
        expiresAt: bundle.expiresAt,
        bundle: persistableBundle(bundle),
      });
    }
    return { bundle, fromCache: false };
  } catch (e) {
    return reportCompiledFallback(dcId, `the compiled payload fetch threw: ${e?.message ?? e}`);
  }
}

export async function prefetchCompiledDependencies(env, rootBundle, descriptors = {}) {
  const ids = [...new Set((rootBundle?.payload?.dependencies ?? []).map(Number))].filter(id => id > 0);
  await Promise.all(ids.map(async dcId => {
    const { bundle } = await loadCompiledBundle(env, {
      dynamicComponentId: dcId,
      version: descriptors?.[dcId]?.version,
    });
    if (bundle) {
      mergeCompiledBundle(bundle);
    }
  }));
}
